import { useDraggable, useDroppable } from '@dnd-kit/core'
import { Icon } from '@natera/material/lib/icon'
import { cn } from '~/shared/lib/cn'

interface SeparatorProps {
  pageIndex: number
  empty: boolean
  dashed: boolean
  solid: boolean
  onSplitClick: () => void
  onUnsplitClick: () => void
}

export function Separator({
  pageIndex,
  empty,
  dashed,
  solid,
  onSplitClick,
  onUnsplitClick,
}: SeparatorProps) {
  const {
    attributes,
    listeners,
    setNodeRef: setDraggableNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `separator-${pageIndex}`,
    data: {
      pageIndex,
    },
    disabled: !solid,
  })

  const { isOver, setNodeRef: setDroppableNodeRef } = useDroppable({
    id: `separator-${pageIndex}`,
    data: {
      pageIndex,
    },
    disabled: !dashed,
  })

  const separatorProps = () => {
    if (solid) {
      return {
        ref: setDraggableNodeRef,
        ...attributes,
        ...listeners,
        style: transform
          ? {
              transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
            }
          : {},
      }
    }
    if (dashed) {
      return {
        ref: setDroppableNodeRef,
      }
    }
    return {}
  }

  return (
    <div
      className={cn('relative flex w-5 justify-center', {
        'bg-surface-lowest': empty,
        'group/separator-dashed bg-surface-low pt-4 pb-8': dashed,
        'group/separator-solid cursor-grab bg-transparent': solid,
        'z-2 cursor-grabbing': isDragging,
        'bg-surface-lowest p-0': isOver,
      })}
      {...separatorProps()}
    >
      {!empty && (
        <>
          <div
            className={cn({
              'w-0.5 bg-[repeating-linear-gradient(to_bottom,_#1ab6ff_0px,_#1ab6ff_7px,_#f6f6f6_7px,_#f6f6f6_14px)] group-hover/separator-dashed:bg-[repeating-linear-gradient(to_bottom,_#0081bd_0px,_#0081bd_7px,_#f6f6f6_7px,_#f6f6f6_14px)]':
                dashed && !isOver,
              'bg-primary-main group-hover/separator-solid:bg-primary-focused-deprecated w-[3px]':
                solid,
            })}
          />
          {dashed && !isOver && (
            <button
              className="bg-primary-main text-surface-lowest absolute top-1/2 -left-0.5 z-1 flex size-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-none opacity-0 transition-opacity group-hover/separator-dashed:opacity-100 hover:shadow-[0px_1.175px_3.055px_rgba(0,0,0,0.32)]"
              onClick={() => {
                onSplitClick()
              }}
            >
              <Icon
                children="content_cut"
                className="text-base"
              />
            </button>
          )}
          {solid && !isDragging && (
            <button
              className="bg-primary-main text-surface-lowest absolute top-1/2 -left-0.5 z-1 flex size-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-none opacity-0 transition-opacity group-hover/separator-solid:opacity-100 hover:shadow-[0px_1.175px_3.055px_rgba(0,0,0,0.32)]"
              onPointerDown={(e) => e.stopPropagation()} // prevent dragging
              onClick={() => {
                onUnsplitClick()
              }}
            >
              <Icon
                children="delete"
                className="text-base"
              />
            </button>
          )}
        </>
      )}
    </div>
  )
}
